import { z } from 'zod'
import { TenantIdSchema, UlidSchema, IsoDateSchema } from './common'

/**
 * Postal address for a supplier. Wholly optional — a casual consignor ("DJ
 * Smitty") may not give one. Stored as a nested object (null when absent) so
 * partial addresses don't litter the supplier with empty top-level columns.
 */
export const SupplierAddressSchema = z
  .object({
    street: z.string().max(120).nullable().default(null),
    postalCode: z.string().max(20).nullable().default(null),
    city: z.string().max(80).nullable().default(null),
    // Free-text rather than an ISO enum: a Dutch shop's consignors are almost
    // all "Nederland", and an open string avoids a code↔label mapping for one
    // rarely-filtered field. Revisit if address ever drives shipping logic.
    country: z.string().max(80).nullable().default(null),
  })
  .openapi('SupplierAddress')

/**
 * A supplier consigns records to the shop. Each shop has at least one
 * supplier (the shop itself, often called the "default" supplier with 100%
 * share). Additional suppliers can be added with their own revenue share
 * percentage and (optionally) their own logo to print on the record label.
 *
 * The contact/address fields below are managed from the admin Settings page
 * (Phase 6). They are all `.nullable().default(null)` so suppliers seeded by
 * the older `upsert-supplier.ts` script — which only wrote name/share/logo —
 * still parse on read.
 */
export const SupplierSchema = z
  .object({
    id: UlidSchema,
    tenantId: TenantIdSchema,
    /**
     * The everyday name the app shows (labels, dropdowns, lists) — the form's
     * "Weergavenaam", e.g. "DJ Smitty" or "Toon". NB: despite this field's
     * bare name it is the SCREEN name, and `displayName` below is the real
     * one — the fields are named opposite to what they hold. Existing tenant
     * data is stored in this orientation, so renaming them is a data
     * migration, not a doc fix.
     */
    name: z.string().min(1).max(100),
    /**
     * Real / legal name — the form's "Naam", e.g. "Toon Luisterbar B.V.".
     * Printed as the selling party on the kassabon (falling back to `name`
     * when absent). See the naming caveat on `name` above.
     */
    displayName: z.string().max(100).nullable().default(null),
    address: SupplierAddressSchema.nullable().default(null),
    phone: z.string().max(40).nullable().default(null),
    email: z.string().email().max(254).nullable().default(null),
    /**
     * Public website URL, printed in the kassabon's seller contact line.
     * Free-format (no `.url()`): shop owners type "www.example.nl" without a
     * scheme, and it's display data, never dereferenced by the app.
     */
    website: z.string().min(1).max(200).nullable().default(null),
    /**
     * Chamber-of-commerce registration number (NL: KvK-nummer). Meaningful
     * mainly on the default "shop" supplier — the selling legal entity — where
     * it's printed on the kassabon; optional for consignors. Free-format:
     * registry formats differ per country and it's display data. Defaulted for
     * read tolerance of rows written before this field existed.
     */
    kvkNumber: z.string().min(1).max(20).nullable().default(null),
    /**
     * VAT identification number (NL: BTW-nummer), e.g. "NL123456789B01".
     * Same kassabon role + free-format + read-tolerance reasoning as
     * kvkNumber.
     */
    vatNumber: z.string().min(1).max(30).nullable().default(null),
    /** Free-text notes for shop staff. Never shown to customers. */
    comments: z.string().max(2000).nullable().default(null),
    /** Percentage of the sale price that goes to the supplier (0..100). */
    defaultSharePercent: z.number().min(0).max(100),
    /**
     * S3 object key of an uploaded logo in the shared media bucket
     * (`<tenantId>/logos/<ulid>.<ext>`). The frontend resolves it to a CDN URL
     * via `mediaUrl()`. This is the canonical logo for suppliers created/edited
     * through the Settings UI; `logoUrl` below is the legacy fallback.
     */
    logoKey: z.string().nullable().default(null),
    /**
     * Legacy logo location: a relative SPA path (e.g. `/logo.png`) or absolute
     * URL written by the seeding script before uploads existed. Display order is
     * `logoKey` (via CDN) → `logoUrl` → hardcoded `/logo.png`.
     */
    logoUrl: z.string().nullable(),
    /** Exactly one supplier per tenant has isDefault=true at any time. */
    isDefault: z.boolean(),
    /** Inactive suppliers stay in the database for historical references but are hidden from the intake dropdown. */
    isActive: z.boolean(),
    createdAt: IsoDateSchema,
  })
  .openapi('Supplier')

/**
 * Editable supplier fields, request-side. Defined explicitly rather than derived
 * from `SupplierSchema` on purpose: the entity carries `.default(null)` on its
 * optional fields for read-tolerance of older rows, and those defaults do NOT
 * survive `.partial()` — a derived `.partial()` schema would coerce every omitted
 * optional to `null`, so a PATCH that only changes `name` would wipe the rest of
 * the contact details and `ERR_EMPTY_UPDATE` would be unreachable. Keeping these
 * `.nullable()` WITHOUT a default means an omitted field stays absent, so a patch
 * is a genuine sparse update.
 */
const supplierWritableFields = {
  name: z.string().min(1).max(100),
  displayName: z.string().max(100).nullable(),
  address: SupplierAddressSchema.nullable(),
  phone: z.string().max(40).nullable(),
  email: z.string().email().max(254).nullable(),
  website: z.string().min(1).max(200).nullable(),
  kvkNumber: z.string().min(1).max(20).nullable(),
  vatNumber: z.string().min(1).max(30).nullable(),
  comments: z.string().max(2000).nullable(),
  defaultSharePercent: z.number().min(0).max(100),
  logoKey: z.string().nullable(),
} as const

/**
 * POST /suppliers body. Server fills id/tenantId/createdAt and forces
 * isDefault=false + isActive=true (the default "shop" supplier is created only
 * by provisioning, never via this form). `logoUrl` is omitted — new suppliers
 * use `logoKey` exclusively. `.strict()` rejects unknown keys so a client can't
 * smuggle isDefault/isActive/tenantId. name + defaultSharePercent are required;
 * the rest are optional (absent ⇒ stored as null by the handler).
 */
export const CreateSupplierRequestSchema = z
  .object({
    name: supplierWritableFields.name,
    displayName: supplierWritableFields.displayName.optional(),
    address: supplierWritableFields.address.optional(),
    phone: supplierWritableFields.phone.optional(),
    email: supplierWritableFields.email.optional(),
    website: supplierWritableFields.website.optional(),
    kvkNumber: supplierWritableFields.kvkNumber.optional(),
    vatNumber: supplierWritableFields.vatNumber.optional(),
    comments: supplierWritableFields.comments.optional(),
    defaultSharePercent: supplierWritableFields.defaultSharePercent,
    logoKey: supplierWritableFields.logoKey.optional(),
  })
  .strict()
  .openapi('CreateSupplierRequest')

/**
 * PATCH /suppliers/{id} body. Every field optional and SPARSE — an omitted field
 * is left untouched (not nulled). isDefault/isActive are NOT editable here —
 * `isDefault` is a provisioning invariant and `isActive` flips only through
 * delete (soft) / restore. `.strict()` catches tampering.
 */
export const UpdateSupplierRequestSchema = z
  .object(supplierWritableFields)
  .partial()
  .strict()
  .openapi('UpdateSupplierRequest')

export const ListSuppliersResponseSchema = z
  .object({
    items: z.array(SupplierSchema),
  })
  .openapi('ListSuppliersResponse')

/**
 * GET /suppliers query. `includeInactive=true` adds soft-deleted suppliers to
 * the result — honored only for admins (the Settings "show archived" view).
 */
export const ListSuppliersQuerySchema = z
  .object({
    includeInactive: z.enum(['true', 'false']).optional(),
  })
  .openapi('ListSuppliersQuery')

/** DELETE /suppliers/{id} result: `deleted` (hard) or `archived` (soft). */
export const DeleteSupplierResponseSchema = z
  .object({
    status: z.enum(['deleted', 'archived']),
    id: UlidSchema,
  })
  .openapi('DeleteSupplierResponse')

export type SupplierAddress = z.infer<typeof SupplierAddressSchema>
export type Supplier = z.infer<typeof SupplierSchema>
export type CreateSupplierRequest = z.infer<typeof CreateSupplierRequestSchema>
export type UpdateSupplierRequest = z.infer<typeof UpdateSupplierRequestSchema>
export type ListSuppliersResponse = z.infer<typeof ListSuppliersResponseSchema>
export type ListSuppliersQuery = z.infer<typeof ListSuppliersQuerySchema>
export type DeleteSupplierResponse = z.infer<typeof DeleteSupplierResponseSchema>
